"use client";

import { Link } from "@/i18n/navigation";

// Pill button with a gradient glow behind the label (adapted from Uiverse).
// Shared by the Hero "Let's talk" CTA, the navbar "Get in touch", and the
// section CTAs.
//
// Two glow treatments, because one does not cover both shapes:
//
//   default — a small circle spinning behind the label. Sized for a compact
//             pill, where the circle is about as wide as the button.
//   wide    — pass `wide` for a full-width button. The circle is a fixed size,
//             so on a 1200px button it shrinks to a smudge floating in the
//             middle rather than lighting the button; this sweeps a tiled
//             gradient across the whole width instead.
//
// Pass `className` to tweak alignment/width per context, and `ariaLabel` where
// the visible label is not the whole story — the Commitment contact buttons say
// "Call Us Now" but announce the number.
//
// The link is the locale-aware one, so "/contact-us" keeps the reader in Thai
// instead of dropping them on the English route. The mailto:/tel: callers are
// safe: next-intl leaves any href carrying a protocol exactly as written.
// Two weights, so a pair of these can say which one is the main way through.
//
// The contact CTAs come in twos — "Talk via Email" beside "Call Us Now" — and
// as two identical white pills the only thing separating them was the label.
// `ghost` is the site's resting-glass control (the same fill and hairline
// FilterPill wears when it is not selected), at exactly the same size and
// radius as `solid`, so the two still read as one pair rather than as two
// unrelated buttons.
//
// The glow belongs to `solid` only. It is a blurred disc sitting BEHIND the
// label, which a filled pill hides until the hover brings it up through the
// fill; with a see-through button there is nothing to come up through, and it
// just reads as a coloured smudge parked inside the outline.
const VARIANT = {
  solid:
    "bg-[#EFEFEF] text-[#252525] shadow-[0_0_7px_-5px_rgba(0,0,0,0.5)] hover:bg-[#c1e4f8] hover:text-[#210055]",
  ghost:
    "bg-white/[0.06] text-white outline outline-1 -outline-offset-1 outline-white/25 backdrop-blur-[10px] hover:bg-white/[0.12] hover:outline-white/40",
};

export default function GlowButton({
  href,
  children,
  className = "",
  onClick,
  wide = false,
  variant = "solid",
  ariaLabel,
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-label={ariaLabel}
      className={`group relative z-0 inline-flex shrink-0 items-center justify-center overflow-hidden rounded-[10rem] px-[30px] py-[15px] text-sm font-bold transition-[transform,background-color,color,outline-color] duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue focus-visible:ring-offset-2 focus-visible:ring-offset-black ${VARIANT[variant]} ${className}`}
    >
      {variant === "ghost" ? null : wide ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-[inherit]"
        >
          {/* Twice the button's width, gradient tiled at half of that (i.e. one
              button-width per tile) — see the glow-sweep keyframe. Taller than
              the button so the blur never shows a hard top or bottom edge.
              Held at 70%: the circle version can sit at full strength because it
              only covers part of a small pill, but across a full-width button
              the same gradient reads as a fill and drags the label's contrast
              down each time the darkest band passes behind it. */}
          <span className="absolute inset-y-[-100%] left-0 w-[200%] bg-[linear-gradient(90deg,#2f79d0_0%,#aebdce_25%,#ffffff_50%,#aebdce_75%,#2f79d0_100%)] bg-[length:50%_100%] opacity-0 blur-[24px] transition-opacity duration-300 group-hover:opacity-70 motion-safe:group-hover:animate-[glow-sweep_3s_linear_infinite]" />
        </span>
      ) : (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center"
        >
          <span className="size-40 rounded-full bg-[linear-gradient(90deg,#2f79d0_0%,#aebdce_49%,#ffffff_100%)] opacity-0 blur-[20px] transition-[width,height,opacity] duration-300 group-hover:size-32 group-hover:opacity-100 motion-safe:group-hover:animate-[spin_3s_linear_infinite]" />
        </span>
      )}
      {/* inline-flex + gap so a caller can put an icon beside the label (the
          mailto:/tel: CTAs do). A label passed as plain text is unaffected —
          gap needs two children before it has anything to space. */}
      <span className="relative z-[2] inline-flex items-center gap-2">
        {children}
      </span>
    </Link>
  );
}
